import { useSyncExternalStore } from 'react';
import { getUnsyncedConfigs, sendDeviceCommand } from '../api/api';

const POLL_INTERVAL_MS = 2000;
const SYNC_TIMEOUT_MS = 10000;

const EMPTY_STATE = { unsyncedConfigs: null, syncing: false, result: null };

// Module-level store keyed by hub serial number, so the workspace bar and the
// Configuration page share one view of the unsynced configs and one sync run.
const states = new Map();
const subscribers = new Set();

const getState = serialNumber => states.get(serialNumber) ?? EMPTY_STATE;

function updateState(serialNumber, patch) {
  states.set(serialNumber, { ...getState(serialNumber), ...patch });
  subscribers.forEach(callback => callback());
}

function subscribe(callback) {
  subscribers.add(callback);
  return () => subscribers.delete(callback);
}

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms));

const errorDetail = error => error.response?.data?.detail || error.message;

// Returns the list of unsynced config paths, or null when the request failed.
export async function refreshUnsyncedConfigs(serialNumber) {
  try {
    const response = await getUnsyncedConfigs(serialNumber);
    const unsyncedConfigs = response.data || [];
    updateState(serialNumber, { unsyncedConfigs });
    return unsyncedConfigs;
  } catch (error) {
    console.error('Error fetching unsynced configs:', error);
    return null;
  }
}

// Ask the hub to sync, poll until every config is synced, then reboot it so the
// new configuration takes effect. Gives up if configs are still unsynced after
// SYNC_TIMEOUT_MS.
export async function syncAndReboot(serialNumber) {
  if (getState(serialNumber).syncing) {
    return;
  }
  const finish = (tone, message) =>
    updateState(serialNumber, { syncing: false, result: { tone, message } });

  updateState(serialNumber, { syncing: true, result: null });
  try {
    await sendDeviceCommand(serialNumber, 'syncall');
  } catch (error) {
    finish('error', `Failed to send the sync command: ${errorDetail(error)}`);
    return;
  }

  const deadline = Date.now() + SYNC_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await sleep(POLL_INTERVAL_MS);
    const unsyncedConfigs = await refreshUnsyncedConfigs(serialNumber);
    if (unsyncedConfigs?.length === 0) {
      try {
        await sendDeviceCommand(serialNumber, 'reboot');
      } catch (error) {
        finish('error', `Configs synced, but the reboot command failed: ${errorDetail(error)}`);
        return;
      }
      finish('success', 'Successfully synced. The hub has been rebooted.');
      return;
    }
  }
  finish('error', 'Failed to sync to the hub. The hub may be offline or not responding.');
}

export function clearSyncResult(serialNumber) {
  updateState(serialNumber, { result: null });
}

// { unsyncedConfigs: string[] | null, syncing: boolean, result: { tone, message } | null }
export default function useHubConfigSync(serialNumber) {
  return useSyncExternalStore(subscribe, () =>
    serialNumber ? getState(serialNumber) : EMPTY_STATE
  );
}
